import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvaluationStore } from '@/store/evaluationStore';
import {
  getEvaluationTask,
  getTaskSongPairs,
  getSongPairVersions,
  submitFamiliarity,
  submitRatings,
  completeTask,
  getEvaluationInstanceConfig,
} from '@/services/evaluationService';
import { ProgressBar } from '@/components/evaluation/ProgressBar';
import { SongPairCard } from '@/components/evaluation/SongPairCard';
import { FamiliaritySelector } from '@/components/evaluation/FamiliaritySelector';
import { VersionPlayer } from '@/components/evaluation/VersionPlayer';
import { RatingMatrix } from '@/components/evaluation/RatingMatrix';

type EvaluationStep = 'familiarity' | 'rating' | 'completed';

export function EvaluationTaskPage() {
  const { instanceId, taskId } = useParams<{ instanceId: string; taskId: string }>();
  const navigate = useNavigate();
  
  const {
    setCurrentTask,
    setSongPairs,
    setCurrentSongPairIndex,
    markFamiliaritySubmitted,
    markRatingsSubmitted,
    getCurrentSongPair,
    getProgress,
  } = useEvaluationStore();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState<EvaluationStep>('familiarity');
  const [versions, setVersions] = useState<any[]>([]);
  const [dimensions, setDimensions] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  
  const currentSongPair = getCurrentSongPair();
  const progress = getProgress();
  
  useEffect(() => {
    if (!taskId || !instanceId) {
      navigate('/evaluation');
      return;
    }
    
    loadTaskData();
  }, [taskId, instanceId]);
  
  const loadTaskData = async () => {
    if (!taskId || !instanceId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // 加载任务详情、歌曲对列表和评估维度配置
      const [taskRes, songPairsRes, configRes] = await Promise.all([
        getEvaluationTask(taskId),
        getTaskSongPairs(taskId),
        getEvaluationInstanceConfig(instanceId),
      ]);
      
      setCurrentTask(taskRes);
      setSongPairs(songPairsRes.songPairs || []);
      setDimensions(configRes.evaluation_dimensions || []);
      
      // 如果有歌曲对，加载第一个歌曲对的版本
      if (songPairsRes.songPairs && songPairsRes.songPairs.length > 0) {
        await loadVersions(taskId, songPairsRes.songPairs[0].id);
      }
      
      // 根据任务状态决定初始步骤
      if (taskRes.status === 'completed') {
        setCurrentStep('completed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };
  
  const loadVersions = async (taskId: string, songPairId: string) => {
    try {
      const versionsRes = await getSongPairVersions(taskId, songPairId);
      setVersions(versionsRes.versions || []);
    } catch (err) {
      console.error('Failed to load versions:', err);
      setVersions([]);
    }
  };
  
  const handleFamiliaritySubmit = async (isFamiliar: boolean, updatedByUser: boolean) => {
    if (!taskId || !currentSongPair) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      await submitFamiliarity(taskId, {
        songPairId: currentSongPair.id,
        trackPrevId: currentSongPair.track_prev,
        trackNextId: currentSongPair.track_next,
        isFamiliar,
        updatedByUser,
      });
      
      markFamiliaritySubmitted(currentSongPair.id);
      setCurrentStep('rating');
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleRatingsSubmit = async (ratings: Array<{ versionId: string; dimension: string; score: number }>) => {
    if (!taskId || !currentSongPair) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      // 为每个评分添加 songPairId
      const ratingsWithPairId = ratings.map(r => ({
        ...r,
        songPairId: currentSongPair.id,
      }));
      
      await submitRatings(taskId, ratingsWithPairId);
      
      markRatingsSubmitted(currentSongPair.id);
      
      // 检查是否还有下一首歌
      const nextIndex = useEvaluationStore.getState().currentSongPairIndex + 1;
      const songPairs = useEvaluationStore.getState().songPairs;
      
      if (nextIndex < songPairs.length) {
        // 移动到下一首歌
        setCurrentSongPairIndex(nextIndex);
        setCurrentStep('familiarity');
        await loadVersions(taskId, songPairs[nextIndex].id);
      } else {
        // 所有歌曲评估完成
        await completeTask(taskId);
        setCurrentStep('completed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500">加载中...</div>
      </div>
    );
  }
  
  if (!currentSongPair) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500">无可用的歌曲对</div>
      </div>
    );
  }
  
  if (currentStep === 'completed') {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border-2 border-green-500 p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold mb-4">评估已完成！</h2>
          <p className="text-gray-600 mb-6">
            感谢您的参与！您的评估数据已保存。
          </p>
          <button
            onClick={() => navigate('/evaluation')}
            className="bg-sky-500 hover:bg-sky-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            返回评估首页
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* 顶部进度条和导航 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/evaluation')}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回
          </button>
          <h1 className="text-2xl font-bold">评估任务</h1>
          <div className="w-20" />
        </div>
        
        <ProgressBar
          current={progress.current}
          total={progress.total}
        />
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {/* 歌曲对信息 */}
      <div className="mb-6">
        <SongPairCard
          trackPrevInfo={currentSongPair.trackPrevInfo}
          trackNextInfo={currentSongPair.trackNextInfo}
          genre={currentSongPair.genre}
        />
      </div>
      
      {/* 熟悉度选择 */}
      {currentStep === 'familiarity' && (
        <div className="mb-6">
          <FamiliaritySelector
            isFamiliar={currentSongPair.isFamiliar}
            onSubmit={handleFamiliaritySubmit}
            disabled={submitting}
          />
        </div>
      )}
      
      {/* 评分阶段 */}
      {currentStep === 'rating' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-bold text-lg mb-4">试听混音版本</h3>
            <div className="space-y-4">
              {versions.map((version) => (
                <VersionPlayer key={version.id} version={version} />
              ))}
            </div>
          </div>
          
          <RatingMatrix
            versions={versions}
            dimensions={dimensions}
            onSubmit={handleRatingsSubmit}
            disabled={submitting}
          />
        </div>
      )}
    </div>
  );
}

